/**
 * Resumable chunked upload helper.
 *
 * - Splits a File into 8 MB chunks
 * - Calls /api/documents/uploads/initiate, then PUTs each chunk in order
 * - Retries each chunk up to 3 times with exponential backoff
 * - Reports progress + can be cancelled via AbortSignal
 *
 * On disconnect mid-upload the caller can re-invoke with the same `sessionId`
 * via `resumeChunkedUpload(sessionId, file)` — the server already knows which
 * chunks it has and we skip those.
 */
import { api } from './api';

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024; // 8 MB

export interface ChunkedUploadOptions {
  documentType?: string;
  category?: string;
  projectId?: string;
  eCTDModule?: string;
  eCTDPath?: string;
  chunkSize?: number;
  onProgress?: (info: { receivedBytes: number; totalBytes: number; pct: number }) => void;
  signal?: AbortSignal;
}

interface InitiateResponse {
  sessionId: string;
  totalChunks: number;
  chunkSize: number;
  expiresAt: string;
}

interface StatusResponse {
  id: string;
  status: 'OPEN' | 'COMPLETED' | 'ABORTED' | 'EXPIRED';
  totalBytes: number;
  receivedBytes: number;
  totalChunks: number;
  receivedChunks: number[];
  chunkSize: number;
  documentId: string | null;
}

export async function chunkedUpload(file: File, opts: ChunkedUploadOptions = {}) {
  const chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE;

  // 1. Initiate
  const initRes = await api.post<InitiateResponse>('/api/documents/uploads/initiate', {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    totalBytes: file.size,
    chunkSize,
    documentType: opts.documentType,
    category: opts.category,
    projectId: opts.projectId,
    eCTDModule: opts.eCTDModule,
  });
  const session = initRes.data;

  return uploadChunksFor(session.sessionId, file, opts);
}

/**
 * Resume an existing session — pulls status, skips chunks already received.
 */
export async function resumeChunkedUpload(
  sessionId: string,
  file: File,
  opts: ChunkedUploadOptions = {},
) {
  return uploadChunksFor(sessionId, file, opts);
}

async function uploadChunksFor(sessionId: string, file: File, opts: ChunkedUploadOptions) {
  // Pull current state to figure out which chunks are still missing.
  const statusRes = await api.get<StatusResponse>(`/api/documents/uploads/${sessionId}`);
  const status = statusRes.data;
  if (status.status !== 'OPEN') {
    if (status.status === 'COMPLETED' && status.documentId) {
      const doc = await api.get(`/api/documents/${status.documentId}`);
      return doc.data;
    }
    throw new Error(`Upload session is ${status.status}`);
  }

  const totalChunks = status.totalChunks;
  const chunkSize = status.chunkSize;
  const totalBytes = status.totalBytes;
  const have = new Set(status.receivedChunks);

  let sentBytes = status.receivedBytes;
  for (let i = 0; i < totalChunks; i += 1) {
    if (opts.signal?.aborted) {
      throw new DOMException('upload aborted', 'AbortError');
    }
    if (have.has(i)) continue;

    const start = i * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const blob = file.slice(start, end);
    const buf = await blob.arrayBuffer();

    await putChunkWithRetry(sessionId, i, buf, opts.signal);

    sentBytes += end - start;
    opts.onProgress?.({
      receivedBytes: sentBytes,
      totalBytes,
      pct: totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0,
    });
  }

  // Finalise.
  const completeRes = await api.post(`/api/documents/uploads/${sessionId}/complete`);
  return completeRes.data;
}

async function putChunkWithRetry(
  sessionId: string,
  chunkIndex: number,
  body: ArrayBuffer,
  signal?: AbortSignal,
): Promise<void> {
  let attempt = 0;
  let lastErr: unknown;
  while (attempt < 3) {
    if (signal?.aborted) throw new DOMException('upload aborted', 'AbortError');
    try {
      await api.put(`/api/documents/uploads/${sessionId}/chunks/${chunkIndex}`, body, {
        headers: { 'Content-Type': 'application/octet-stream' },
        signal,
      });
      return;
    } catch (err) {
      lastErr = err;
      attempt += 1;
      if (attempt >= 3) break;
      // Exponential backoff: 500 ms, 1.5 s, 4.5 s
      const delay = 500 * 3 ** (attempt - 1);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

export const CHUNKED_UPLOAD_THRESHOLD = 50 * 1024 * 1024; // 50 MB
